export default function SecurityDepositTransfer() {
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-safe-line"></i>
            <span>Financial Protection</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Security Deposit Transfer Requirements and Best Practices
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Proper handling of security deposits is legally required and critical to avoiding liability. Both seller and buyer can be held responsible for deposit returns if transfers aren't documented correctly.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Legal Requirements for Security Deposit Transfers</h3>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              Security deposits are tenant property held in trust by the landlord. When you sell your apartment building, you must transfer all security deposits to the new owner along with complete accounting records. Failure to do so properly can result in both parties being liable to tenants for the full deposit amounts plus penalties.
            </p>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mb-6">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line text-red-600 text-2xl mt-1"></i>
                <div>
                  <h4 className="font-semibold text-red-900 mb-2">Critical Legal Warning</h4>
                  <p className="text-red-800 text-sm leading-relaxed">
                    In most states, if security deposits are not properly transferred, BOTH the seller and buyer can be held liable to return the full deposit amount to tenants. This means tenants could potentially collect twice - once from you and once from the new owner.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">Step-by-Step Transfer Process</h3>

            <div className="space-y-4">
              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Create Complete Security Deposit Ledger</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Document every security deposit with tenant name, unit number, deposit amount, date received, and current balance. Include any interest accrued if required by state law.
                    </p>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Required Information:</p>
                      <ul className="text-xs text-slate-600 space-y-1">
                        <li>• Tenant full legal name</li>
                        <li>• Unit number and address</li>
                        <li>• Original deposit amount and date</li>
                        <li>• Current deposit balance</li>
                        <li>• Accrued interest (if applicable)</li>
                        <li>• Any deductions or claims made</li>
                        <li>• Bank account where deposits are held</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Reconcile All Deposits</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Verify that the total amount in your security deposit account matches the sum of all individual tenant deposits. Resolve any discrepancies before closing.
                    </p>
                    <div className="bg-amber-50 p-3 rounded-lg">
                      <p className="text-xs text-amber-800">
                        <strong>Common Issue:</strong> If deposits are short, seller must bring cash to closing to make up the difference. This is non-negotiable.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Execute Security Deposit Transfer Agreement</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Both parties sign a formal agreement acknowledging the transfer of all security deposits. This document should be part of your closing paperwork.
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-file-text-line text-teal-600"></i>
                        <span>Legal document</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-shield-check-line text-teal-600"></i>
                        <span>Liability protection</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                    4
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Transfer Funds at Closing</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Security deposit funds are typically transferred via closing statement credit or separate wire transfer. The buyer must establish a new security deposit account to receive these funds.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                    5
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Notify Tenants in Writing</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Within the timeframe required by state law (typically 5-30 days), send written notice to all tenants confirming the security deposit transfer and providing new owner contact information.
                    </p>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-600 italic">
                        "Your security deposit of $[amount] has been transferred to the new owner, [Name], and is being held in accordance with state law. Contact information: [details]"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/62279d9d4124fdf3d4d3648f97d757e2.jpg"
                  alt="Security deposit transfer accounting and documentation for apartment building sale"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-calculator-line text-teal-600 text-xl"></i>
                Security Deposit Calculation Example
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="text-slate-600">12 units × $1,500 deposit</span>
                  <span className="font-semibold text-slate-900">$18,000</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="text-slate-600">Accrued interest (if required)</span>
                  <span className="font-semibold text-slate-900">+ $450</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="text-slate-600">Previous deductions</span>
                  <span className="font-semibold text-slate-900">- $800</span>
                </div>
                <div className="flex justify-between items-center pt-2 bg-teal-50 p-3 rounded-lg">
                  <span className="font-semibold text-slate-900">Total to Transfer</span>
                  <span className="font-bold text-teal-600 text-lg">$17,650</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-map-pin-line text-blue-600 text-xl"></i>
                State-Specific Requirements
              </h4>
              <div className="space-y-3 text-sm">
                <div className="bg-white p-3 rounded-lg">
                  <p className="font-semibold text-slate-900 mb-1">California</p>
                  <p className="text-slate-600 text-xs">Must pay interest on deposits; 21-day notice to tenants; separate account required</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="font-semibold text-slate-900 mb-1">New York</p>
                  <p className="text-slate-600 text-xs">Interest required for buildings 6+ units; must be held in NY bank; annual interest statements</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="font-semibold text-slate-900 mb-1">Florida</p>
                  <p className="text-slate-600 text-xs">30-day notice to tenants; choice of separate account or surety bond; no interest required</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="font-semibold text-slate-900 mb-1">Texas</p>
                  <p className="text-slate-600 text-xs">30-day notice; no separate account required; no interest required; must provide forwarding address</p>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-3">
                <i className="ri-information-line"></i> Always verify current state and local requirements with your attorney
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Common Security Deposit Transfer Problems and Solutions</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-start gap-3 mb-3">
                <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                <h4 className="font-semibold text-slate-900">Problem: Insufficient Funds in Deposit Account</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                The security deposit account balance is less than the total of all tenant deposits, often due to commingling funds or unauthorized withdrawals.
              </p>
              <div className="bg-white p-4 rounded-lg border-l-4 border-teal-500">
                <p className="text-xs font-semibold text-teal-600 mb-2">Solution:</p>
                <p className="text-xs text-slate-600 mb-2">
                  Seller must bring cash to closing to cover the shortfall. This is typically handled as a debit on the closing statement. Consider this when pricing your property.
                </p>
                <p className="text-xs text-slate-600">
                  <strong>Prevention:</strong> Maintain deposits in a separate, dedicated account and never commingle with operating funds.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-start gap-3 mb-3">
                <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                <h4 className="font-semibold text-slate-900">Problem: Missing Deposit Records</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                No documentation exists for some tenant deposits, or records are incomplete or contradictory.
              </p>
              <div className="bg-white p-4 rounded-lg border-l-4 border-teal-500">
                <p className="text-xs font-semibold text-teal-600 mb-2">Solution:</p>
                <p className="text-xs text-slate-600 mb-2">
                  Contact tenants to verify deposit amounts. Have them sign deposit confirmation statements. If disputes arise, assume the higher amount to avoid liability.
                </p>
                <p className="text-xs text-slate-600">
                  <strong>Prevention:</strong> Maintain detailed records from day one, including copies of deposit checks and receipts.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-start gap-3 mb-3">
                <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                <h4 className="font-semibold text-slate-900">Problem: Interest Calculation Disputes</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                In states requiring interest on deposits, calculations are incorrect or interest hasn't been tracked properly.
              </p>
              <div className="bg-white p-4 rounded-lg border-l-4 border-teal-500">
                <p className="text-xs font-semibold text-teal-600 mb-2">Solution:</p>
                <p className="text-xs text-slate-600 mb-2">
                  Hire an accountant to calculate correct interest amounts using state-mandated rates. Transfer the full amount including interest to buyer.
                </p>
                <p className="text-xs text-slate-600">
                  <strong>Prevention:</strong> Track interest monthly and provide annual statements to tenants as required by law.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-start gap-3 mb-3">
                <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                <h4 className="font-semibold text-slate-900">Problem: Tenant Disputes About Deposit Amounts</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Tenants claim they paid different deposit amounts than what's recorded in your books.
              </p>
              <div className="bg-white p-4 rounded-lg border-l-4 border-teal-500">
                <p className="text-xs font-semibold text-teal-600 mb-2">Solution:</p>
                <p className="text-xs text-slate-600 mb-2">
                  Review original lease agreements and deposit receipts. If tenant can provide proof of higher deposit, you must honor it. Consider escrow holdback for disputed amounts.
                </p>
                <p className="text-xs text-slate-600">
                  <strong>Prevention:</strong> Always provide written receipts for deposits and keep copies in tenant files.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
